import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { GoogleGenerativeAI } from "@google/generative-ai";
import { 
  ArrowRight, 
  Star, 
  Users, 
  Building, 
  Target,
  Zap,
  Globe,
  Shield,
  Heart,
  Search,
  CheckCircle,
  Clock,
  Sparkles,
  Calendar,
  FileText,
  Bot,
  Briefcase,
  TrendingUp
} from 'lucide-react';

interface CareerGrowthChartProps {
  currentRole?: string;
  targetRole?: string;
  timeframe?: string;
  milestones?: string[];
  progress?: number;
  onViewPotentialRoles?: () => void;
}

export function CareerGrowthChart({
  currentRole = 'Not Set',
  targetRole = 'Not Set',
  timeframe = '2 years',
  milestones = [],
  progress = 0,
  onViewPotentialRoles
}: CareerGrowthChartProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [insights, setInsights] = useState<any>(null);

  useEffect(() => {
    if (user && currentRole && targetRole) {
      fetchCareerInsights();
    }
  }, [user, currentRole, targetRole]);

  const fetchCareerInsights = async () => {
    if (!import.meta.env.VITE_GEMINI_API_KEY) {
      console.error('Gemini API key not configured');
      return;
    }

    setLoading(true);
    try {
      // Get career path data from database
      const { data: pathData } = await supabase
        .from('career_paths')
        .select(`
          *
        `)
        .eq('profile_id', user?.id)
        .eq('current_role', currentRole)
        .eq('target_role', targetRole)
        .maybeSingle();

      // Initialize Gemini
      const genAI = new GoogleGenerativeAI(import.meta.env.VITE_GEMINI_API_KEY);
      const model = genAI.getGenerativeModel({ model: "gemini-2.0-flash" });

      // Generate insights
      const prompt = `
        Analyze this career transition and provide insights:
        Current Role: ${currentRole}
        Target Role: ${targetRole}
        Timeframe: ${timeframe}
        Current Progress: ${progress}%

        Return a JSON object with:
        - Required skills to develop
        - Recommended milestones
        - Potential challenges
        - Growth opportunities
        - Estimated timeline
      `;

      const result = await model.generateContent(prompt);
      const response = await result.response;
      const text = response.text();
      
      // Clean and parse the response
      const cleanJson = text
        .replace(/^```json\s*/gim, '')
        .replace(/^```\s*/gim, '')
        .replace(/\s*```$/gim, '')
        .trim();

      const insights = JSON.parse(cleanJson);

      // Save insights to database
      if (pathData) {
        await supabase
          .from('career_paths')
          .update({
            insights,
            updated_at: new Date().toISOString()
          })
          .eq('id', pathData.id);
      }

      setInsights(insights);
    } catch (error) {
      console.error('Error fetching career insights:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load career insights'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            <CardTitle>Career Growth</CardTitle>
          </div>
          <Button variant="outline" size="sm" onClick={onViewPotentialRoles}>
            View Potential Roles
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Current Progress */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Current Role</span>
            </div>
            <p className="text-lg font-semibold">{currentRole}</p>
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Target Role</span>
            </div>
            <p className="text-lg font-semibold">{targetRole}</p>
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Progress</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-2 flex-1 bg-secondary rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary rounded-full transition-all duration-500 ease-in-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="text-sm font-medium">{progress}%</span>
            </div>
          </div>
        </div>

        {/* Milestones */}
        {milestones && milestones.length > 0 && (
          <div className="space-y-3">
            <h4 className="font-medium flex items-center gap-2">
              <Star className="h-4 w-4 text-primary" />
              Key Milestones
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {milestones.map((milestone, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg"
                >
                  <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
                  <span>{milestone}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* AI Insights */}
        {insights && (
          <div className="space-y-4 pt-4 border-t">
            <div className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-primary" />
              <h4 className="font-medium">AI Career Insights</h4>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardContent className="p-4">
                  <h5 className="font-medium mb-2 flex items-center gap-2">
                    <Star className="h-4 w-4 text-amber-500" />
                    Required Skills
                  </h5>
                  <div className="flex flex-wrap gap-1">
                    {insights.requiredSkills?.map((skill: string, i: number) => (
                      <Badge key={i} variant="secondary">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <h5 className="font-medium mb-2 flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-emerald-500" />
                    Growth Opportunities
                  </h5>
                  <ul className="space-y-2">
                    {insights.growthOpportunities?.map((opp: string, i: number) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-emerald-500" />
                        {opp}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Next Step */}
        <div className="flex justify-end">
          <Button asChild>
            <Link to="/planner">
              Start Career Plan
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}